/**
 * Insights on the phone — the tracker's screen, same order, same emphasis.
 *
 * Hours lead, because that is the number you actually came for; money is the
 * quiet figure beside it. Then a seven-day band split by project, then the
 * projects themselves with a share bar each. Tapping a project opens its
 * entries rather than filtering here, so there is one place to correct them.
 *
 * The unbilled total is pinned above the tab bar: it is the one figure worth
 * seeing without scrolling, and it is what turns into an invoice. It is ALL
 * billable work not yet marked invoiced, whatever the period above says —
 * last month's unbilled hours do not stop being owed a bill because you
 * paged forward.
 */

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useChrome } from "@/context/ChromeContext";
import { useProjects } from "@/hooks/useData";
import { useTimeEntries } from "@/hooks/useTime";
import {
  breakdownText, entryDayKey, groupBy, hours, inBounds, money, periodBounds, toIsoLocal,
  totalsOf, type Period,
} from "@/lib/timeRollup";

import { SectionLabel, Segmented, screen } from "../components";

const RANGES: { value: Period; label: string }[] = [
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "quarter", label: "Quarter" },
];
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function MobileInsights() {
  const navigate = useNavigate();
  const { data: entries = [] } = useTimeEntries();
  const { data: projects = [] } = useProjects();

  const [range, setRange] = useState<Period>("week");
  const [offset, setOffset] = useState(0);

  const bounds = useMemo(() => periodBounds(range, offset), [range, offset]);
  const shown = useMemo(() => entries.filter((e) => inBounds(e, bounds)), [entries, bounds]);
  const totals = totalsOf(shown, projects);
  const owed = useMemo(() => totalsOf(entries, projects), [entries, projects]);
  const activeDays = new Set(shown.map((e) => entryDayKey(e))).size;
  // Share of the time that was for clients — billable, project work or no charge.
  const clientPct = totals.seconds > 0 ? Math.round((totals.clientSeconds / totals.seconds) * 100) : 0;

  /**
   * A seven-day band for the chosen period: its own week when a week is
   * selected, otherwise the last week of that month or quarter — today's week
   * for the current one, so the common case still reads as "this week".
   */
  const band = useMemo(() => {
    const anchor = bounds.end.getTime() > Date.now() ? new Date() : bounds.end;
    const weekStart = range === "week" ? bounds.start : periodBounds("week", 0, anchor).start;
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      const key = toIsoLocal(d);
      const rows = entries.filter((e) => entryDayKey(e) === key);
      const segs = [...groupBy(rows, (e) => e.project || "__adhoc")]
        .map(([id, list]) => ({ id, seconds: list.reduce((n, e) => n + (e.durationSeconds || 0), 0) }))
        .sort((a, b) => b.seconds - a.seconds);
      return {
        key, label: DAY_LABELS[i], segs,
        total: rows.reduce((n, e) => n + (e.durationSeconds || 0), 0),
      };
    });
  }, [entries, bounds, range]);
  const bandMax = Math.max(3600, ...band.map((d) => d.total));
  const bandProjects = [...new Set(band.flatMap((d) => d.segs.map((s) => s.id)))];

  const byProject = useMemo(
    () => [...groupBy(shown.filter((e) => e.project), (e) => e.project!)]
      .map(([id, rows]) => ({ id, project: projects.find((p) => p.id === id), totals: totalsOf(rows, projects) }))
      .sort((a, b) => b.totals.seconds - a.totals.seconds),
    [shown, projects],
  );

  useChrome({ title: "Insights", subtitle: bounds.label }, [bounds.label]);

  const colorOf = (id: string) => projects.find((p) => p.id === id)?.color ?? "var(--muted3)";
  const nameOf = (id: string) => (id === "__adhoc" ? "Ad-hoc" : projects.find((p) => p.id === id)?.name ?? "—");

  return (
    <div style={{ ...screen, paddingBottom: "calc(var(--above-tabbar) + 120px)" }}>
      <Segmented value={range} onChange={(v) => { setRange(v); setOffset(0); }} options={RANGES} />

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
        <button onClick={() => setOffset((o) => o - 1)} aria-label="Previous period" style={pager}>‹</button>
        <span className="mono" style={{ flex: 1, textAlign: "center", fontSize: 13.5, color: "var(--ink2)" }}>
          {bounds.label}
        </span>
        <button
          onClick={() => setOffset((o) => Math.min(0, o + 1))}
          disabled={bounds.isCurrent}
          aria-label="Next period"
          style={{ ...pager, opacity: bounds.isCurrent ? 0.3 : 1 }}
        >›</button>
      </div>

      {/* Hours lead. */}
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginTop: 18, flexWrap: "wrap" }}>
        <div className="mono" style={{ fontSize: 36, fontWeight: 600, letterSpacing: "-0.02em", lineHeight: 1 }}>
          {hours(totals.seconds)}{" "}
          <span style={{ fontSize: 15, fontWeight: 500, color: "var(--muted)", letterSpacing: "normal" }}>hours</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <div style={{ fontSize: 13, color: "var(--ink)" }}>logged {bounds.isCurrent ? "this period" : bounds.label}</div>
          <div className="mono" style={{ fontSize: 11.5, color: "var(--muted)" }}>
            {breakdownText(totals, hours) || "nothing logged"}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 18, marginTop: 12, flexWrap: "wrap" }}>
        <Figure label="Client work" value={totals.seconds > 0 ? `${clientPct}%` : "—"} />
        <Figure label="Average a day" value={activeDays > 0 ? `${hours(totals.seconds / activeDays)} h` : "—"} />
        <Figure label="Days worked" value={String(activeDays)} />
      </div>

      <SectionLabel>Daily hours, by project</SectionLabel>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 7, height: 112 }}>
        {band.map((d) => (
          <div key={d.key} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", height: "100%" }}>
            {d.total === 0 ? (
              <div style={{ width: "100%", height: 2, borderRadius: 999, background: "var(--line)" }} />
            ) : (
              <div
                title={`${d.key} — ${hours(d.total)} h`}
                style={{
                  width: "100%", height: `${Math.max(6, (d.total / bandMax) * 100)}%`,
                  borderRadius: "4px 4px 0 0", overflow: "hidden",
                  display: "flex", flexDirection: "column", justifyContent: "flex-end",
                }}
              >
                {d.segs.map((s) => (
                  <div key={s.id} style={{ height: `${(s.seconds / d.total) * 100}%`, background: colorOf(s.id) }} />
                ))}
              </div>
            )}
            <span style={{ fontSize: 12, fontWeight: 500, color: "var(--muted)", marginTop: 6 }}>{d.label}</span>
          </div>
        ))}
      </div>

      {bandProjects.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 12px", marginTop: 12 }}>
          {bandProjects.map((id) => (
            <span key={id} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: colorOf(id) }} />
              <span style={{ fontSize: 11, color: "var(--muted)" }}>{nameOf(id)}</span>
            </span>
          ))}
        </div>
      )}

      <SectionLabel right={`${byProject.length}`}>By project</SectionLabel>
      {byProject.length === 0 ? (
        <div style={{ padding: "24px 2px", fontSize: 13, color: "var(--muted)" }}>Nothing in this period.</div>
      ) : byProject.map(({ id, project, totals: t }) => {
        const share = totals.seconds > 0 ? Math.round((t.seconds / totals.seconds) * 100) : 0;
        return (
          <button
            key={id}
            onClick={() => navigate(`/entries?project=${encodeURIComponent(id)}&all=1`)}
            style={{
              display: "flex", flexDirection: "column", gap: 8, width: "100%", minHeight: 52,
              padding: "11px 2px", textAlign: "left", cursor: "pointer",
              border: "none", borderBottom: "1px solid var(--tint2)", background: "none",
              WebkitTapHighlightColor: "transparent",
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 9 }}>
              <span style={{ flex: "none", width: 8, height: 8, borderRadius: 2, background: project?.color ?? "var(--muted3)" }} />
              <span style={{ flex: 1, minWidth: 0, fontSize: 14, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {project?.name ?? "—"}
              </span>
              <span className="mono" style={{ flex: "none", fontSize: 16, fontWeight: 600 }}>{hours(t.seconds)}h</span>
              <span className="mono" style={{ flex: "none", minWidth: 56, textAlign: "right", fontSize: 11.5, color: "var(--muted)" }}>
                {t.amount > 0 ? money(t.amount, t.currency) : "—"}
              </span>
            </span>
            <span style={{ marginLeft: 17, height: 4, borderRadius: 999, background: "var(--tint2)", overflow: "hidden" }}>
              <span style={{ display: "block", height: "100%", width: `${share}%`, background: project?.color ?? "var(--muted3)", borderRadius: 999 }} />
            </span>
          </button>
        );
      })}

      {/* The figure worth seeing without scrolling. */}
      <div style={{
        position: "fixed", left: 12, right: 12, bottom: "var(--above-tabbar)", height: 48,
        display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 18px",
        border: "1px solid var(--line)", borderRadius: 999, background: "var(--surface)",
        boxShadow: "0 6px 18px rgba(20,22,26,0.10)",
      }}>
        <span style={{ fontSize: 12.5, color: "var(--muted)" }}>
          Not yet invoiced{owed.unbilledSeconds > 0 ? ` · ${hours(owed.unbilledSeconds)} h` : ""}
        </span>
        <span className="mono" style={{ fontSize: 15, fontWeight: 500, color: "var(--ink2)" }}>
          {owed.unbilledAmount > 0 ? money(owed.unbilledAmount, owed.currency) : "—"}
        </span>
      </div>
    </div>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--muted2)" }}>{label}</span>
      <span className="mono" style={{ fontSize: 16, fontWeight: 600 }}>{value}</span>
    </span>
  );
}

const pager: React.CSSProperties = {
  flex: "none", width: 44, minHeight: 40, borderRadius: 9, cursor: "pointer",
  border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink2)", fontSize: 16,
};
